/**
 * @file CarrierDiagnosticsPanel.tsx
 * @description Блок «Проверка настроек» перевозчика (phase19.12).
 *
 * Требование пользователя: кнопка, которая проверяет, что интеграция реально
 * работает. Каждая проверка — отдельная строка со статусом, чтобы было видно,
 * что именно чинить: авторизация, среда, город отправления, пробный расчёт,
 * тарифы, кэш ПВЗ, подписка на статусы, локальные параметры.
 *
 * Живые вызовы тратят квоту интеграции → только по кнопке, не при открытии.
 */

import { AlertTriangle, CheckCircle2, Loader2, ShieldQuestion, XCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  diagnoseCarrier,
  type CarrierDiagnosticsResult,
  type CarrierKind,
  type DiagnosticStatus,
} from "../../lib/api-shop-carriers";

interface Props {
  carrier: CarrierKind;
  /** ТК ещё не подключена к магазину — проверять нечего. */
  disabled?: boolean;
  /**
   * Счётчик внешних изменений (правка подписки на вебхук). При росте проверка
   * повторяется — но только если её уже запускали: живые вызовы жгут квоту
   * интеграции, сами их не инициируем.
   */
  rerunToken?: number;
}

const ICONS: Record<DiagnosticStatus, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  fail: XCircle,
};

const TONES: Record<DiagnosticStatus, string> = {
  ok: "text-emerald-600",
  warn: "text-amber-600",
  fail: "text-red-600",
};

export function CarrierDiagnosticsPanel({ carrier, disabled, rerunToken = 0 }: Props) {
  const notify = useNotify();
  const [result, setResult] = useState<CarrierDiagnosticsResult | null>(null);
  const [running, setRunning] = useState(false);
  const [ranAt, setRanAt] = useState<string | null>(null);

  const run = useCallback(async (): Promise<void> => {
    setRunning(true);
    try {
      setResult(await diagnoseCarrier(carrier));
      setRanAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Проверка не выполнена");
    } finally {
      setRunning(false);
    }
  }, [carrier, notify]);

  // Перезапуск после внешнего изменения: индикатор подписки должен позеленеть
  // без второго клика. Реагируем только на реальный рост токена.
  const seenToken = useRef(rerunToken);
  useEffect(() => {
    if (rerunToken === seenToken.current) return;
    seenToken.current = rerunToken;
    if (disabled || !result) return;
    void run();
  }, [rerunToken, disabled, result, run]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">Проверка настроек</div>
          <p className="text-xs text-on-surface-variant">
            Делает живые запросы к перевозчику — расходует квоту интеграции.
          </p>
        </div>
        <Button onClick={() => void run()} disabled={running || disabled}>
          {running ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Проверяем…
            </>
          ) : (
            "Проверить"
          )}
        </Button>
      </div>

      {disabled && (
        <Alert severity="info">
          Сначала подключите перевозчика к магазину и сохраните настройки.
        </Alert>
      )}

      {!result && !disabled && (
        <div className="flex items-center gap-2 rounded-md border border-dashed p-4 text-sm text-on-surface-variant">
          <ShieldQuestion className="size-4 shrink-0" />
          Проверка ещё не запускалась.
        </div>
      )}

      {result && (
        <>
          <div className="flex items-center justify-between text-xs text-on-surface-variant">
            <span>{ranAt ? `Запущена в ${ranAt}` : ""}</span>
            <span className={result.ok ? TONES.ok : TONES.fail}>
              {result.ok ? "Готово к работе" : "Есть блокирующие проблемы"}
            </span>
          </div>
          <ul className="flex flex-col divide-y rounded-md border">
            {result.checks.map((check) => {
              const Icon = ICONS[check.status];
              return (
                <li key={check.key} className="flex items-start gap-3 p-3">
                  <Icon className={`mt-0.5 size-4 shrink-0 ${TONES[check.status]}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{check.title}</div>
                    <div className="text-xs text-on-surface-variant">{check.message}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
